import {
  getTeamPlayerIds,
  getSlotGroup,
  getEloLevel,
  groupColor,
  getPlayerKMultiplier,
  formatBoost,
  EngineSettings,
  SCORE_MIN,
  SCORE_MAX,
} from '../../engine';
import { highlightMatch } from './highlightMatch';
import { useT } from '../../i18n';

// ─────────────────────────────────────────────────────────────────────
// UniversalMatchRow — extracted verbatim from the original artifact (legacy/App.jsx).
//
// Universal match-row component. Layout matches the Padel24 table style:
//   meta | home-players | home-elo | home-score | vs | away-score | away-elo |
//   away-players | extras
//
// Engine swaps (original artifact → src/engine named imports, same functions):
//   getTeamPlayerIds / getSlotGroup / getEloLevel / groupColor  → '../../engine'
//   Padel24Engine.elo.getPlayerKMultiplier → getPlayerKMultiplier
//   Padel24Engine.elo.formatBoost          → formatBoost
//   Padel24Engine.elo.NEWBIE_MATCH_THRESHOLD → EngineSettings.NEWBIE_MATCH_THRESHOLD
//   highlightMatch → './highlightMatch'
// ─────────────────────────────────────────────────────────────────────

export interface UniversalMatchRowProps {
  match: any;
  playerById: any;
  onPlayerClick?: (p: any) => void;
  onScoreClick?: () => void;
  onInfoClick?: () => void;
  showLevel?: boolean;
  showElo?: boolean;
  showTeamElo?: boolean;
  showEloChange?: boolean;
  metaLabel?: any;
  metaSubLabel?: any;
  extras?: any;
  isAlt?: boolean;
  editingScore?: boolean;
  scoreInputs?: any;
  tournamentKMul?: number;
  playerSearch?: string;
}

export function UniversalMatchRow({
  match,
  playerById,
  onPlayerClick,
  onScoreClick,
  // onInfoClick is part of the documented prop surface (UniversalMatchRowProps)
  // but the original body never read it — left out of the destructure to keep
  // lint clean without changing the public API.
  showLevel = true,
  showElo = true,
  showTeamElo = true,
  showEloChange = true,
  metaLabel,
  metaSubLabel,
  extras,
  isAlt = false,
  editingScore = false,
  scoreInputs = null,
  // kMultiplier from the tournament config — affects how long the newbie
  // boost lasts. Americano (0.5) gives a 40-match boost, Padel24 (1.0) gives 20.
  tournamentKMul = 1.0,
  // Search text — if set and the player matches, the player name is shown with
  // a yellow highlight background. Empty = no highlight.
  playerSearch = '',
}: UniversalMatchRowProps) {
  const t = useT();
  // Get player IDs from both team1 and team2
  let [t1p1Id, t1p2Id] = getTeamPlayerIds(match.team1);
  let [t2p1Id, t2p2Id] = getTeamPlayerIds(match.team2);
  // LEAGUE PLAY: if the match has a frozen `roster` (a player in the pair was
  // swapped out AFTER this match), we show the players who ACTUALLY played — not
  // the current pair. This way the substitute is not credited for matches before the swap.
  const hasRoster = !!(
    match.roster &&
    (Array.isArray(match.roster.t1) || Array.isArray(match.roster.t2))
  );
  if (hasRoster) {
    if (Array.isArray(match.roster.t1) && match.roster.t1.length === 2)
      [t1p1Id, t1p2Id] = match.roster.t1;
    if (Array.isArray(match.roster.t2) && match.roster.t2.length === 2)
      [t2p1Id, t2p2Id] = match.roster.t2;
  }

  // Get players
  const t1p1 = playerById[t1p1Id];
  const t1p2 = playerById[t1p2Id];
  const t2p1 = playerById[t2p1Id];
  const t2p2 = playerById[t2p2Id];

  // Winner info
  const completed = match.completed || (match.score1 != null && match.score2 != null);
  const t1Won = completed && match.score1 > match.score2;
  const t2Won = completed && match.score2 > match.score1;

  // Team-average ELO (use pre-match if available)
  const t1Avg =
    match.teamElo1Pre != null
      ? match.teamElo1Pre
      : t1p1 && t1p2
        ? Math.round((t1p1.elo + t1p2.elo) / 2)
        : null;
  const t2Avg =
    match.teamElo2Pre != null
      ? match.teamElo2Pre
      : t2p1 && t2p2
        ? Math.round((t2p1.elo + t2p2.elo) / 2)
        : null;

  // ELO changes (actually applied, includes new-player boost)
  const eloChange1 = match.eloChange1 ?? match.result?.t1Change;
  const eloChange2 = match.eloChange2 ?? match.result?.t2Change;

  // Per-team newbieMul stored ON THE MATCH. Used to compute the base number
  // (without boost) for the team pill, and the actually-boosted number for the
  // player's 2x pill. If the match lacks this field (old matches), default 1.0.
  const t1MatchNewbieMul = match.t1NewbieMul ?? match.result?.t1NewbieMul ?? 1.0;
  const t2MatchNewbieMul = match.t2NewbieMul ?? match.result?.t2NewbieMul ?? 1.0;

  // Base ELO change (the "pure" number without boost) — shown in the team pill.
  // The boosted change (what was actually added/subtracted) is shown behind
  // the player's 2x pill.
  const t1UnboostedChange =
    eloChange1 != null && t1MatchNewbieMul > 0
      ? Math.round(eloChange1 / t1MatchNewbieMul)
      : eloChange1;
  const t2UnboostedChange =
    eloChange2 != null && t2MatchNewbieMul > 0
      ? Math.round(eloChange2 / t2MatchNewbieMul)
      : eloChange2;

  // Get the ELO at the time for each player (pre-match) if stored
  const playerEloPre = match.playerEloPre || match.result?.playerEloPre || {};
  const getDisplayElo = (id, fallback) => {
    if (id == null) return null;
    if (playerEloPre[id] != null) return playerEloPre[id];
    return fallback;
  };

  // Render one player cell
  // teamChange = the team's actual ELO change (incl. boost). Used to show the
  // player's actual impact behind the 2x pill for boosted players.
  const renderPlayer = (p, isWinner, slot, displayElo, teamChange) => {
    if (!p) return <div className="text-xs text-ink-3 italic">—</div>;
    const eloToShow = displayElo != null ? displayElo : p.elo;
    const groupLabel = getSlotGroup(slot); // Padel24 groups (A, B, C...)
    const level = showLevel && !groupLabel ? getEloLevel(eloToShow) : null;

    // Check whether the player has a new-player boost (newbieMul > 1).
    // We pass tournamentKMul so the threshold scales correctly for
    // americano (40 matches) vs Padel24 (20 matches) vs club championship (13).
    const newbieMul = getPlayerKMultiplier(p, Date.now(), tournamentKMul);
    const isBoosted = newbieMul > 1;

    return (
      <div className="flex items-center gap-1.5 min-w-0">
        {/* Group pill (Padel24) or level pill (Americano/single) */}
        {groupLabel ? (
          <span
            className="font-mono text-[9px] font-bold px-1 rounded flex-shrink-0"
            style={{ background: groupColor(groupLabel).bg, color: groupColor(groupLabel).text }}
            title={`Gruppe ${groupLabel}`}
          >
            {groupLabel}
          </span>
        ) : (
          level && (
            <span
              className="font-mono text-[9px] font-bold px-1 rounded flex-shrink-0"
              style={{ background: level.color, color: level.text }}
              title={`Nivå ${level.level} — ${level.name}`}
            >
              {level.level}
            </span>
          )
        )}

        {/* Player name (clickable) — placeholders get italic + orange to make them stand out.
            If playerSearch matches, only the matching letters are highlighted. */}
        {(() => {
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPlayerClick?.(p);
              }}
              className={`text-xs truncate text-left hover:underline ${isWinner ? 'font-bold text-ink' : ''} ${p.isPlaceholder ? 'italic text-orange' : ''}`}
              style={{
                background: 'transparent',
                padding: 0,
                border: 'none',
                cursor: 'pointer',
                minWidth: 0,
              }}
              title={
                p.isPlaceholder
                  ? `Reservert plass — klikk for å fylle inn navn`
                  : `Vis profil for ${p.name}`
              }
            >
              {highlightMatch(p.name, playerSearch)}
            </button>
          );
        })()}

        {/* New-player badge after the player name + actual ELO impact right behind it.
            The team pill in the middle shows the base number (without boost) — this pill
            shows how much the player actually got, because the boost applies to the whole team. */}
        {isBoosted && (
          <span
            className="font-mono text-[9px] font-bold px-1 rounded flex-shrink-0 inline-flex items-center gap-1"
            style={{ background: '#fef3c7', color: '#92400e' }}
            title={`Ny spiller-boost ${formatBoost(newbieMul)} — avtar lineært over kamper, blir 1x når spilleren har spilt ${EngineSettings.NEWBIE_MATCH_THRESHOLD} kamper (justert for turneringens ELO-vekt).`}
          >
            <span>{formatBoost(newbieMul)}</span>
            {completed && teamChange != null && (
              <span
                style={{
                  color: teamChange > 0 ? '#3f6212' : teamChange < 0 ? '#9f1239' : '#92400e',
                }}
              >
                {teamChange > 0 ? '+' : ''}
                {teamChange}
              </span>
            )}
          </span>
        )}

        {/* ELO */}
        {showElo && (
          <span className="text-[10px] font-mono text-ink-3 flex-shrink-0">{eloToShow}</span>
        )}
      </div>
    );
  };

  // The 2x badge is now shown behind the PLAYER NAME (the renderPlayer function) —
  // gives better context about who is actually the newbie. The team cell shows
  // only the average ELO and the change.
  const renderTeamElo = (teamAvg, change, isStronger) => {
    if (!showTeamElo) return <div />;
    const eloColor = isStronger ? 'text-lime-deep' : 'text-ink-3';
    return (
      <div className="text-center self-center leading-tight">
        {teamAvg != null ? (
          <div className={`font-mono text-xs font-semibold ${eloColor}`}>{teamAvg}</div>
        ) : (
          <div className="text-ink-3 text-xs">–</div>
        )}
        {showEloChange && completed && change != null && (
          <div
            className={`font-mono text-[11px] font-semibold ${change > 0 ? 'text-lime-deep' : change < 0 ? 'text-rose' : 'text-ink-3'}`}
          >
            {change > 0 ? '+' : ''}
            {change}
          </div>
        )}
      </div>
    );
  };

  // Determine which team is stronger (for the green color on team ELO)
  const t1IsStronger = t1Avg != null && t2Avg != null && t1Avg > t2Avg;
  const t2IsStronger = t1Avg != null && t2Avg != null && t2Avg > t1Avg;

  // Score cell for one team
  // Modes:
  //   - editingScore + scoreInputs given: show input field
  //   - completed: show number (clickable to change)
  //   - otherwise: show "+" if onScoreClick is given
  const renderScoreCell = (sideValue, sideKey, isWinner) => {
    if (editingScore && scoreInputs) {
      const val = sideKey === 1 ? scoreInputs.s1 : scoreInputs.s2;
      const onChange = sideKey === 1 ? scoreInputs.onChange1 : scoreInputs.onChange2;
      const onFocus = sideKey === 1 ? scoreInputs.onFocus1 : scoreInputs.onFocus2;
      return (
        <input
          type="number"
          min={SCORE_MIN}
          max={SCORE_MAX}
          inputMode="numeric"
          value={val}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && scoreInputs.onSubmit) scoreInputs.onSubmit();
          }}
          onBlur={(e) => {
            // Auto-save when the user leaves both score inputs for the SAME match.
            // If focus moves to the other score input in the same match (same data attribute),
            // do nothing — wait until both are filled and the user has left the match.
            const next: any = e.relatedTarget;
            const nextMatchId = next?.dataset?.scoreMatchId;
            const currentMatchId = String(match.id);
            if (nextMatchId === currentMatchId) return;
            // Both must be filled with valid numbers
            if (scoreInputs.s1 === '' || scoreInputs.s2 === '') return;
            const n1 = parseInt(scoreInputs.s1, 10);
            const n2 = parseInt(scoreInputs.s2, 10);
            if (isNaN(n1) || isNaN(n2) || n1 < 0 || n2 < 0) return;
            if (scoreInputs.onSubmit) scoreInputs.onSubmit();
          }}
          data-score-match-id={String(match.id)}
          data-score-side={String(sideKey)}
          className="w-full text-center font-mono font-bold py-1 px-1 border border-line rounded focus-ring focus:border-ink"
          style={{ fontSize: 14 }}
          title={
            scoreInputs.autoFillsOpponent
              ? `Auto-fylles til ${scoreInputs.pointsPerGame}`
              : undefined
          }
        />
      );
    }

    if (completed) {
      return (
        <button
          onClick={onScoreClick}
          className={`font-mono font-bold text-base ${isWinner ? 'text-lime-deep' : 'text-ink-3'} ${onScoreClick ? 'hover:underline cursor-pointer' : 'cursor-default'}`}
          style={{ background: 'transparent', border: 'none', padding: 0 }}
        >
          {sideValue}
        </button>
      );
    }

    if (onScoreClick) {
      return (
        <button
          onClick={onScoreClick}
          aria-label={t('tournament.match.enterScore')}
          className="text-xs font-mono text-ink-3 px-2 py-1 rounded hover:bg-bg-soft hover:text-ink"
          style={{ border: 'none', background: 'transparent' }}
        >
          +
        </button>
      );
    }

    return <span className="text-xs text-ink-4 font-mono">–</span>;
  };

  // Club colors for Club Americano. If the match has team1ClubId/team2ClubId,
  // we show a thin colored border on the left/right edge of the player cells.
  // Club A = green, Club B = blue. For now hardcoded based on the order in
  // config.clubs[].
  const t1ClubColor = match.team1ClubId ? '#22c55e' : null; // club A
  const t2ClubColor = match.team2ClubId ? '#3b82f6' : null; // club B
  const clubBgT1 = t1ClubColor ? '#dcfce740' : 'transparent';
  const clubBgT2 = t2ClubColor ? '#dbeafe40' : 'transparent';

  // Grid: meta | t1-p1 | t1-p2 | t1-elo | t1-score | vs | t2-score | t2-elo | t2-p1 | t2-p2 | extras
  // The player columns MUST have a minimum width so names don't shrink away.
  // The whole row therefore gets a minimum width of ~680px — below that the
  // parent container scrolls horizontally.
  const gridCols =
    '80px minmax(110px, 1fr) minmax(110px, 1fr) 56px 56px 20px 56px 56px minmax(110px, 1fr) minmax(110px, 1fr)' +
    (extras ? ' 32px' : '');

  return (
    <div
      className={`grid items-stretch gap-0.5 px-3 py-2 text-sm hover:bg-bg-soft/40 border-b border-line/40 last:border-b-0 ${isAlt ? 'bg-bg-soft/20' : ''}`}
      style={{ gridTemplateColumns: gridCols, minWidth: extras ? '712px' : '680px' }}
    >
      {/* Meta */}
      <div className="flex flex-col justify-center text-left">
        {metaLabel && (
          <div className="font-mono text-[10px] font-bold text-ink-2 uppercase tracking-wider">
            {metaLabel}
          </div>
        )}
        {metaSubLabel && <div className="font-mono text-[10px] text-ink-3">{metaSubLabel}</div>}
      </div>

      {/* Home players — get the team's actual ELO change (with boost) behind the 2x pill.
          Background = club color when the match has team1ClubId (Club Americano). */}
      <div
        className="self-center"
        style={{
          background: clubBgT1,
          paddingLeft: t1ClubColor ? '4px' : 0,
          borderLeft: t1ClubColor ? `2px solid ${t1ClubColor}` : 'none',
        }}
      >
        {renderPlayer(t1p1, t1Won, match.team1?.p1, getDisplayElo(t1p1Id, t1p1?.elo), eloChange1)}
      </div>
      <div className="self-center" style={{ background: clubBgT1 }}>
        {renderPlayer(t1p2, t1Won, match.team1?.p2, getDisplayElo(t1p2Id, t1p2?.elo), eloChange1)}
      </div>

      {/* Home Team ELO — shows the BASE ELO without new-player boost.
          If t1NewbieMul > 1 the team was boosted, and the base number is computed
          as actual-change / boost-multiplier. */}
      {renderTeamElo(t1Avg, t1UnboostedChange, t1IsStronger)}

      {/* Home Score */}
      <div className="text-center self-center">{renderScoreCell(match.score1, 1, t1Won)}</div>

      {/* vs */}
      <div className="text-center text-[10px] text-ink-3 font-mono self-center">vs</div>

      {/* Away Score */}
      <div className="text-center self-center">{renderScoreCell(match.score2, 2, t2Won)}</div>

      {/* Away Team ELO — base number without boost */}
      {renderTeamElo(t2Avg, t2UnboostedChange, t2IsStronger)}

      {/* Away players — the team's actual change (with boost) behind the 2x pill */}
      <div
        className="self-center"
        style={{
          background: clubBgT2,
          paddingLeft: t2ClubColor ? '4px' : 0,
          borderLeft: t2ClubColor ? `2px solid ${t2ClubColor}` : 'none',
        }}
      >
        {renderPlayer(t2p1, t2Won, match.team2?.p1, getDisplayElo(t2p1Id, t2p1?.elo), eloChange2)}
      </div>
      <div className="self-center" style={{ background: clubBgT2 }}>
        {renderPlayer(t2p2, t2Won, match.team2?.p2, getDisplayElo(t2p2Id, t2p2?.elo), eloChange2)}
      </div>

      {/* Extras (info button, save button in editing) */}
      {extras && <div className="self-center flex justify-center">{extras}</div>}
    </div>
  );
}
